"use client"
import type {
  DefaultValues,
  FormState,
  UseFormReturn,
} from "react-hook-form"
import type { z } from "zod"
import type { Dependency, FieldConfig } from "./types"
import type {
  ZodObjectOrWrapped,
} from "./utils"

import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import {
  useForm,
} from "react-hook-form"
import { Button } from "@/components/ui/button"

import { Form } from "@/components/ui/form"
import { cn } from "@/lib/utils"
import AutoFormObject from "./fields/object"
import {
  getDefaultValues,
  getObjectFormSchema,
} from "./utils"

export function AutoFormSubmit({
  children,
  className,
  disabled,
}: {
  children?: React.ReactNode
  className?: string
  disabled?: boolean
}) {
  return (
    <Button
      className={className}
      disabled={disabled}
      type="submit"
    >
      {children ?? "Submit"}
    </Button>
  )
}

function AutoForm<SchemaType extends ZodObjectOrWrapped>({
  formSchema,
  values: valuesProp,
  onValuesChange: onValuesChangeProp,
  onParsedValuesChange,
  onSubmit: onSubmitProp,
  fieldConfig,
  children,
  className,
  dependencies,
  layout = "vertical",
  columns = 1,
  containerClassName,
}: {
  formSchema: SchemaType
  values?: Partial<z.infer<SchemaType>>
  onValuesChange?: (
    values: Partial<z.infer<SchemaType>>,
    form: UseFormReturn<z.infer<SchemaType>>
  ) => void
  onParsedValuesChange?: (
    values: Partial<z.infer<SchemaType>>,
    form: UseFormReturn<z.infer<SchemaType>>
  ) => void
  onSubmit?: (
    values: z.infer<SchemaType>,
    form: UseFormReturn<z.infer<SchemaType>>
  ) => void
  fieldConfig?: FieldConfig<z.infer<SchemaType>>
  children?:
    | React.ReactNode
    | ((formState: FormState<z.infer<SchemaType>>) => React.ReactNode)
  className?: string
  dependencies?: Dependency<z.infer<SchemaType>>[]
  layout?: "vertical" | "grid"
  columns?: number
  containerClassName?: string
}) {
  const objectFormSchema = getObjectFormSchema(formSchema)
  const defaultValues: DefaultValues<z.infer<typeof objectFormSchema>> | null
    = getDefaultValues(objectFormSchema, fieldConfig)

  const form = useForm<z.infer<typeof objectFormSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues ?? undefined,
    values: valuesProp,
    mode: "onChange",
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    const parsedValues = formSchema.safeParse(values)
    if (parsedValues.success) {
      onSubmitProp?.(parsedValues.data, form)
    }
  }

  React.useEffect(() => {
    const subscription = form.watch((values) => {
      onValuesChangeProp?.(values, form)
      const parsedValues = formSchema.safeParse(values)
      if (parsedValues.success) {
        onParsedValuesChange?.(parsedValues.data, form)
      }
    })

    return () => subscription.unsubscribe()
  }, [form, formSchema, onValuesChangeProp, onParsedValuesChange])

  const renderChildren
    = typeof children === "function"
      ? children(form.formState as FormState<z.infer<SchemaType>>)
      : children

  return (
    <div className="w-full">
      <Form {...form}>
        <form
          className={cn("space-y-5", className)}
          onSubmit={(e) => {
            void form.handleSubmit(onSubmit)(e)
          }}
        >
          <AutoFormObject
            columns={columns}
            containerClassName={containerClassName}
            dependencies={dependencies}
            fieldConfig={fieldConfig}
            form={form}
            layout={layout}
            schema={objectFormSchema}
          />

          {renderChildren}
        </form>
      </Form>
    </div>
  )
}

export default AutoForm
